import React, { useContext } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  useLocation,
  useRoutes,
} from "react-router-dom";
import AuthLayout from "../layouts/auth-layout";
import DashboardLayout from "../layouts/dashboard-layout";
import Login from "../pages/auth/login";
import ForgotPassword from "../pages/auth/forgot-password";
import UpdatePassword from "../pages/auth/update-password";
import MainLayout from "../layouts/main-layout";
import RoleList from "../pages/dashboard/roles/list";
import UserList from "../pages/dashboard/users/list";
import LogsList from "../pages/dashboard/logs/list";
import NotFound from "../components/not-found/not-found";
import Forbidden from "../components/forbidden/forbidden";
import { UserContext } from "../context";
import ResetPassword from "../pages/auth/reset-password";
import EncDecUtility from "../pages/dashboard/utility";
import EntityList from "../pages/dashboard/entities/list";
import DashboardView from "../pages/dashboard/dash";
import SettingsForm from "../components/layout-header/settings-form";
import FileManagerList from "../pages/dashboard/file-manager/list";
import ProductList from "../pages/dashboard/configuration/list";
import HomeList from "../pages/dashboard/home/list";
import Level1Form from "../pages/dashboard/evaluation/l1-form";
import ReportList from "../pages/dashboard/reports/list";
import OverallList from "../pages/dashboard/overall-list/list";
import ArchivedList from "../pages/dashboard/overall-list/archived-list";

const routes = [
  {
    path: "/login",
    element: (
      <AuthLayout>
        <Login />
      </AuthLayout>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <AuthLayout>
        <ForgotPassword />
      </AuthLayout>
    ),
  },
  {
    path: "/update-password",
    element: (
      <MainLayout>
        <UpdatePassword />
      </MainLayout>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <MainLayout>
        <ResetPassword />
      </MainLayout>
    ),
  },
  {
    path: "/dashboard",
    element: (
      <DashboardLayout>
        <DashboardView />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/settings",
    element: (
      <DashboardLayout>
        <SettingsForm />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/entities",
    element: (
      <DashboardLayout>
        <EntityList />
      </DashboardLayout>
    ),
  },
  // {
  //   path: "/dashboard/l1form",
  //   element: (
  //     <DashboardLayout>
  //       <Level1Form />
  //     </DashboardLayout>
  //   ),
  // },
  {
    path: "/dashboard/file-manager",
    element: (
      <DashboardLayout>
        <FileManagerList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/home",
    element: (
      <DashboardLayout>
        <HomeList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/configuration",
    element: (
      <DashboardLayout>
        <ProductList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/users",
    element: (
      <DashboardLayout>
        <UserList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/reports",
    element: (
      <DashboardLayout>
        <ReportList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/overall-list",
    element: (
      <DashboardLayout>
        <OverallList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/archived-records",
    element: (
      <DashboardLayout>
        <ArchivedList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/roles",
    element: (
      <DashboardLayout>
        <RoleList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/logs",
    element: (
      <DashboardLayout>
        <LogsList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/utility",
    element: (
      <DashboardLayout>
        <EncDecUtility />
      </DashboardLayout>
    ),
  },
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    path: "*",
    element: (
      <DashboardLayout>
        <NotFound />
      </DashboardLayout>
    ),
  },
  {
    path: "/forbidden",
    element: (
      <DashboardLayout>
        <Forbidden />
      </DashboardLayout>
    ),
  },
];

const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const userData: any = useContext(UserContext);
  const authorizedRoutes =
    userData?.menuHierarchy?.map((item: any) =>
      item?.entityUrl.toLocaleLowerCase()
    ) || [];

  const location = useLocation();
  const currentPath = location.pathname.toLocaleLowerCase();

  // Define excluded paths
  const excludedPaths = [
    // "/dashboard/dealers/form",
    "/dashboard/utility",
    "/dashboard/settings",
    // "/dashboard/file-manager", //TODO :: WILL BE REMOVED ONCE MENU HEIRARCHY ADDED IN DB
    // "/dashboard/products", //TODO :: WILL BE REMOVED ONCE MENU HEIRARCHY ADDED IN DB
    // "/dashboard/home", //TODO :: WILL BE REMOVED ONCE MENU HEIRARCHY ADDED IN DB
    // "/dashboard/l1form", //TODO :: WILL BE REMOVED ONCE MENU HEIRARCHY ADDED IN DB
  ];

  // Check if the current path is excluded
  const isExcluded = excludedPaths.includes(currentPath);

  // Check authorization only for non-excluded paths
  const isAuthorized = isExcluded || authorizedRoutes?.includes(currentPath);

  if (!isAuthorized && authorizedRoutes?.length > 0) {
    return <Navigate to="/forbidden" replace />;
  }

  return element;
};

const AppRoutes: React.FC = () => {
  const element = useRoutes(
    routes.map((route) => ({
      ...route,
      element: route.path.startsWith("/dashboard") ? (
        <ProtectedRoute element={route.element} />
      ) : (
        route.element
      ),
    }))
  );

  return <>{element}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

export default App;
